import React, { useRef, useEffect } from 'react';
import { PaymentMethod } from '../../StripePaymentInterface';
import './StripeView.scss';
import StripePoweredBy from '../PoweredBy/StripePoweredBy';
import { useTranslation } from 'react-i18next';
import cardOnFileIcon from '../../../../../../../assets/icons/cards-onfile-icon.svg';
import deleteCardIcon from '../../../../../../../assets/icons/new_delete.svg';
import { Pencil } from 'src/latest/src';
import Input from 'src/components/base/Input/Input';
import { filterKey } from 'src/latest/src/features/billing-admin/billing-admin-home/BIllingAdminConstant';
import { cardStatusValues } from '../../StripePaymentConstant';

interface StripeViewProps {
  existingCard: [PaymentMethod];
  handleUpdateCard: () => void;
  handleCardDelete: (cardId: string) => void;
  handleSetDefaultCard: (cardId: string) => void;
  clientId: string;
  editingCardId: string | null;
  nickname: string;
  nicknameError: string;
  isUpdatingNickname: boolean;
  onEditNickname: (card: PaymentMethod) => void;
  onSaveNickname: (cardId: string) => void;
  onNicknameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onNicknameKeyPress: (e: React.KeyboardEvent, cardId: string) => void;
  onCancelNicknameEdit: () => void;
  hideUpdateButton?: boolean;
}

const StripeView: React.FC<StripeViewProps> = ({
  existingCard,
  handleUpdateCard,
  handleCardDelete,
  handleSetDefaultCard,
  editingCardId,
  nickname,
  nicknameError,
  isUpdatingNickname,
  onEditNickname,
  onSaveNickname,
  onNicknameChange,
  onNicknameKeyPress,
  hideUpdateButton = false,
}) => {
  const { t } = useTranslation('stripePayment');
  const nicknameRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        nicknameRef.current &&
        !nicknameRef.current.contains(event.target as Node) &&
        editingCardId
      ) {
        onSaveNickname(editingCardId);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingCardId, onSaveNickname]);

  return (
    <div className="existing-card">
      <div className="existing-card-details">
        <div className="existing-card-details-onfile">
          <div>
            <img src={cardOnFileIcon} alt="card on file" />
          </div>
          <div>
            <span>{t('stripeView.cardsOnFile')}</span>
          </div>
        </div>
        {existingCard &&
          existingCard.length > 0 &&
          existingCard.map((card: PaymentMethod, index: number) => {
            const isEditing = editingCardId === card.id;

            return (
              <div
                key={index}
                className="existing-card-details-info d-flex flex-column"
              >
                <div className="existing-card-details-label">
                  <div
                    className="existing-card-details-label-nickname"
                    ref={isEditing ? nicknameRef : null}
                  >
                    {isEditing ? (
                      <div
                        className={`nickname-edit-container ${
                          isUpdatingNickname ? 'updating' : ''
                        }`}
                      >
                        <Input
                          value={nickname}
                          onChange={onNicknameChange}
                          onBlur={() => onSaveNickname(card.id)}
                          onKeyDown={(e: React.KeyboardEvent) =>
                            onNicknameKeyPress(e, card.id)
                          }
                          name="cardNickname"
                          feedbackMessage={nicknameError}
                          isInvalid={!!nicknameError}
                          className="nickname-input"
                          placeholder={t('stripeView.nickNamePlaceholder')}
                          disabled={isUpdatingNickname}
                          maxLength={35}
                        />
                      </div>
                    ) : (
                      <div className="nickname-display-container">
                        <div className="nickname-text-wrapper">
                          {card?.card_nick_name &&
                          card?.card_nick_name?.length > 0 ? (
                            <div className="nickname-text">
                              {card.card_nick_name}
                            </div>
                          ) : (
                            <div className="placeholder-text">
                              {t('stripeView.label')}
                            </div>
                          )}
                          <div>
                            <Pencil
                              onClick={() => onEditNickname(card)}
                              className="edit-icon"
                            />
                          </div>
                        </div>
                        <div
                          className={`existing-card-details-label-status ${
                            card.card_status === filterKey?.VALID_CARD
                              ? 'active'
                              : card.card_status === filterKey?.EXPIRED_CARD
                              ? 'expired'
                              : card.card_status === filterKey?.CARD_DECLINED
                              ? 'declined'
                              : card.card_status === filterKey?.CONSENT_PENDING
                              ? 'pending'
                              : ''
                          }`}
                        >
                          <span>
                            {card.card_status === filterKey?.VALID_CARD
                              ? cardStatusValues?.ACTIVE
                              : card.card_status === filterKey?.EXPIRED_CARD
                              ? cardStatusValues?.EXPIRED
                              : card.card_status === filterKey?.CARD_DECLINED
                              ? cardStatusValues?.DECLINED
                              : card.card_status === filterKey?.CONSENT_PENDING
                              ? cardStatusValues?.PENDING
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    {card.is_default ? (
                      <span className="existing-card-details-label-type">
                        {t('stripeView.primaryLabel')}
                      </span>
                    ) : (
                      <span
                        className="on-hover"
                        onClick={() => handleSetDefaultCard(card.id)}
                      >
                        {t('stripeView.markAsPrimary')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="existing-card-details-info-card">
                  <div
                    className={`existing-card-details-content ${
                      card?.is_default ? 'stripe-default-card' : ''
                    }`}
                  >
                    <span className="existing-card-details-card-last4">
                      xxxx xxxx xxxx{' '}
                      <span className="existing-card-details-card-last4-digits">
                        {card?.last_4_digits}
                      </span>
                    </span>
                    <span className="existing-card-details-card-exp-date">
                      {card?.exp_month}/{card?.exp_year?.toString()?.slice(-2)}
                    </span>
                  </div>
                  <div className="cursor-pointer">
                    <img
                      src={deleteCardIcon}
                      alt="Delete card"
                      onClick={() => handleCardDelete(card.id)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      {!hideUpdateButton && (
        <StripePoweredBy
          buttonLabel={t('stripeView.buttonLabel.update')}
          handleSubmit={handleUpdateCard}
          fromView={true}
        />
      )}
    </div>
  );
};

export default StripeView;
